"use client";

import { useMemo, useEffect } from "react";
import { DailyLog, UserProfile } from "@/types";
import { ArrowLeft, Trophy, Flame, Calendar, Activity, Star, Medal, Target, Footprints, Zap, Droplets, Moon, Beef, Map, User, Swords, Shield, Compass, Brain } from "lucide-react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { calculateDailyScore, calculateDailyProtein } from "@/utils/scoring";

interface AchievementsScreenProps {
    logs: DailyLog[];
    profile: UserProfile;
    onBack: () => void;
}

interface Achievement {
    id: string;
    title: string;
    description: string;
    icon: any;
    condition: (stats: Stats) => boolean;
    color: string;
}

interface Stats {
    totalLogs: number;
    currentStreak: number;
    bestStreak: number;
    avgScore: number;
    totalWorkouts: number;
    totalSleepHours: number;
    perfectDays: number; // Score > 90
    totalSteps: number;
    totalCalories: number;
    totalWater: number;
    startDate: string | null;
    totalProtein: number;
    totalDistance: number;
    totalScore: number;
}

export default function AchievementsScreen({ logs, profile, onBack }: AchievementsScreenProps) {

    const stats = useMemo(() => {
        const sortedLogs = [...logs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const totalLogs = sortedLogs.length;

        let currentStreak = 0;
        let bestStreak = 0;
        let tempStreak = 0;
        let totalScore = 0;
        let totalWorkouts = 0;
        let totalSleepHours = 0;
        let perfectDays = 0;
        let totalSteps = 0;
        let totalCalories = 0;
        let totalWater = 0;
        let totalProtein = 0;
        let totalDistance = 0;

        // Basic stats loop
        sortedLogs.forEach((log, index) => {
            // Score
            const score = calculateDailyScore(log);
            totalScore += score;
            if (score >= 90) perfectDays++;

            // Workouts & Steps & Calories
            if (log.workouts && log.workouts.length > 0) {
                log.workouts.forEach(w => {
                    if (w.duration > 0) totalWorkouts++;
                    
                    // Calculate calories: Use logged value OR estimate (approx 6 cals/min)
                    if (w.calories > 0) {
                        totalCalories += w.calories;
                    } else if (w.duration > 0) {
                        totalCalories += w.duration * 6;
                    }
                });
            }

            // Estimate Distance (0.762m per step)
            if (log.steps) {
                totalSteps += log.steps;
                totalDistance += log.steps * 0.000762;
            }

            // Sleep
            if (typeof log.sleep === 'number') totalSleepHours += log.sleep;
            else if (typeof log.sleep === 'object') totalSleepHours += log.sleep.hours;

            // Water
            if (log.water) totalWater += log.water;

            // Protein
            totalProtein += calculateDailyProtein(log);

            // Streak Logic (simplified: consecutive dates)
            if (index === 0) {
                tempStreak = 1;
            } else {
                const prevDate = new Date(sortedLogs[index - 1].date);
                const currDate = new Date(log.date);
                const diffTime = Math.abs(currDate.getTime() - prevDate.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 1) {
                    tempStreak++;
                } else {
                    bestStreak = Math.max(bestStreak, tempStreak);
                    tempStreak = 1;
                }
            }
        });
        bestStreak = Math.max(bestStreak, tempStreak);

        // Determine current streak (check if last log was today or yesterday)
        const today = new Date().toISOString().split('T')[0];
        const lastLogDate = sortedLogs.length > 0 ? sortedLogs[sortedLogs.length - 1].date : null;

        if (lastLogDate === today) {
            currentStreak = tempStreak;
        } else if (lastLogDate) {
            // Check if yesterday
            const yest = new Date();
            yest.setDate(yest.getDate() - 1);
            const yestStr = yest.toISOString().split('T')[0];
            if (lastLogDate === yestStr) {
                currentStreak = tempStreak;
            } else {
                currentStreak = 0;
            }
        }

        return {
            totalLogs,
            currentStreak,
            bestStreak,
            avgScore: totalLogs > 0 ? Math.round(totalScore / totalLogs) : 0,
            totalWorkouts,
            totalSleepHours: Math.round(totalSleepHours),
            perfectDays,
            totalSteps,
            totalCalories,
            totalWater: Math.round(totalWater * 10) / 10, // Round to 1 decimal
            startDate: sortedLogs.length > 0 ? sortedLogs[0].date : null,
            totalProtein: Math.round(totalProtein),
            totalDistance: Math.round(totalDistance * 10) / 10,
            totalScore
        };
    }, [logs]);

    const achievements: Achievement[] = [
        {
            id: "first_step",
            title: "First Step",
            description: "Log your first day",
            icon: Star,
            condition: (s) => s.totalLogs >= 1,
            color: "text-blue-400"
        },
        {
            id: "week_warrior",
            title: "Week Warrior",
            description: "Log 7 days in a row",
            icon: Flame,
            condition: (s) => s.bestStreak >= 7,
            color: "text-orange-500"
        },
        {
            id: "consistency_is_key",
            title: "Consistency is Key",
            description: "30 total logs tracked",
            icon: Calendar,
            condition: (s) => s.totalLogs >= 30,
            color: "text-green-400"
        },
        {
            id: "iron_will",
            title: "Iron Will",
            description: "Complete 10 workouts",
            icon: Activity,
            condition: (s) => s.totalWorkouts >= 10,
            color: "text-red-500"
        },
        {
            id: "sleep_master",
            title: "Sleep Master",
            description: "Track 100+ hours of sleep",
            icon: Trophy,
            condition: (s) => s.totalSleepHours >= 100,
            color: "text-purple-400"
        },
        {
            id: "perfectionist",
            title: "Perfectionist",
            description: "Achieve a 90+ day score",
            icon: Medal,
            condition: (s) => s.perfectDays >= 1,
            color: "text-yellow-400"
        },
        {
            id: "on_fire",
            title: "On Fire",
            description: "Maintain a 14 day streak",
            icon: Flame,
            condition: (s) => s.currentStreak >= 14,
            color: "text-red-600"
        },
        {
            id: "longevity_legend",
            title: "Longevity Legend",
            description: "Track 100 days total",
            icon: Target,
            condition: (s) => s.totalLogs >= 100,
            color: "text-cyan-400"
        },
        {
            id: "hydration_hero",
            title: "Hydration Hero",
            description: "Drink 100L of water",
            icon: Droplets,
            condition: (s) => s.totalWater >= 100,
            color: "text-blue-500"
        },
        {
            id: "pathfinder",
            title: "Pathfinder",
            description: "Walk 50k steps total",
            icon: Footprints,
            condition: (s) => s.totalSteps >= 50000,
            color: "text-emerald-400"
        },
        {
            id: "voyager",
            title: "Voyager",
            description: "Travel 100km total",
            icon: Map,
            condition: (s) => s.totalDistance >= 100,
            color: "text-indigo-500"
        },
        {
            id: "protein_power",
            title: "Protein Power",
            description: "Consume 5kg protein",
            icon: Beef,
            condition: (s) => s.totalProtein >= 5000,
            color: "text-red-500"
        },
        {
            id: "high_voltage",
            title: "High Voltage",
            description: "Maintain >80 avg score",
            icon: Zap,
            condition: (s) => s.avgScore >= 80,
            color: "text-yellow-300"
        },
        {
            id: "furnace",
            title: "The Furnace",
            description: "Burn 50k calories",
            icon: Flame,
            condition: (s) => s.totalCalories >= 50000,
            color: "text-orange-600"
        }
    ];

    // Sync unlocked achievements
    const unlockedIds = useMemo(() => {
        // @ts-ignore - profile might not have achievements typed fully yet if using old UserProfile
        const existing = (profile as any).achievements || [];
        return new Set(existing.map((a: any) => a.id));
    }, [profile]);

    useEffect(() => {
        achievements.forEach(achievement => {
            const isUnlocked = achievement.condition(stats);
            if (isUnlocked && !unlockedIds.has(achievement.id)) {
                // Check if we already tried unlocking this session to avoid loop
                const sessionUnlocked = sessionStorage.getItem(`unlocked_${achievement.id}`);
                if (sessionUnlocked) return;

                const token = localStorage.getItem('token');
                if (!token) return;

                fetch(`${process.env.NEXT_PUBLIC_API_BASE || '/api'}/users/achievements/unlock`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ achievementId: achievement.id })
                }).then(res => {
                    if (res.ok) {
                        sessionStorage.setItem(`unlocked_${achievement.id}`, 'true');
                        console.log(`Unlocked ${achievement.title}`);
                    }
                }).catch(err => console.error("Failed to unlock achievement", err));
            }
        });
    }, [stats, unlockedIds, achievements]);

    // Character Gamification Logic
    const character = useMemo(() => {
        const totalXP = stats.totalScore + (stats.totalWorkouts * 10) + (stats.totalLogs * 5);
        const level = Math.floor(totalXP / 500) + 1;
        const nextLevelXP = level * 500;
        const progress = ((totalXP % 500) / 500) * 100;

        // Determine Archetype
        // Ranger: Steps/Distance weighted
        // Warrior: Workouts/Protein weighted
        // Sage: Sleep/Mindfulness/Water weighted
        // Guardian: Balanced consistency (Streak/Logs) weighted

        const rangerScore = (stats.totalSteps / 1000) + (stats.totalDistance * 10);
        const warriorScore = (stats.totalWorkouts * 20) + (stats.totalProtein / 50) + (stats.totalCalories / 200);
        const sageScore = (stats.totalSleepHours * 2) + (stats.totalWater * 10) + (stats.perfectDays * 5);
        const guardianScore = (stats.totalLogs * 5) + (stats.currentStreak * 10) + (stats.avgScore * 2);

        let archetype = "Guardian";
        let icon = Shield; // Guardian default
        let color = "text-yellow-400";
        let maxScore = guardianScore;

        if (rangerScore > maxScore) {
            archetype = "Ranger";
            icon = Compass;
            color = "text-emerald-400";
            maxScore = rangerScore;
        }
        if (warriorScore > maxScore) {
            archetype = "Warrior";
            icon = Swords;
            color = "text-red-500";
            maxScore = warriorScore;
        }
        if (sageScore > maxScore) {
            archetype = "Sage";
            icon = Brain;
            color = "text-indigo-400";
            maxScore = sageScore;
        }

        return {
            level,
            totalXP,
            nextLevelXP,
            progress,
            archetype,
            icon,
            color,
            title: `Level ${level} ${archetype}`
        };
    }, [stats]);


    return (
        <div className="flex flex-col h-full glass rounded-2xl overflow-hidden relative shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-5 bg-black/40 backdrop-blur-md border-b border-white/5 shrink-0 relative z-10">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-1 -ml-1 text-text-muted hover:text-white transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Trophy size={20} className="text-yellow-500" />
                        Achievements
                    </h2>
                </div>
                {stats.startDate && (
                    <div className="flex flex-col items-end">
                        <span className="text-[10px] text-text-muted uppercase tracking-wider">Since</span>
                        <span className="text-xs font-bold text-white">
                            {new Date(stats.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                    </div>
                )}
            </div>

            {/* Content w/ Character Profile */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-hide">

                {/* Character Profile Card */}
                <div className="glass-card relative overflow-hidden group">
                    {/* Background Glow */}
                    <div className={clsx("absolute top-0 right-0 w-32 h-32 blur-[60px] opacity-20 rounded-full -mr-10 -mt-10 transition-colors duration-500",
                        character.archetype === "Ranger" ? "bg-emerald-500" :
                            character.archetype === "Warrior" ? "bg-red-500" :
                                character.archetype === "Sage" ? "bg-indigo-500" :
                                    "bg-yellow-500"
                    )}></div>

                    <div className="relative z-10 flex items-center gap-5">
                        <div className="relative">
                            {/* Avatar Image */}
                            <div className={clsx("w-24 h-24 rounded-2xl flex items-center justify-center border-2 shadow-2xl bg-black/40 relative overflow-hidden",
                                character.archetype === "Ranger" ? "border-emerald-500/50 shadow-emerald-500/20" :
                                    character.archetype === "Warrior" ? "border-red-500/50 shadow-red-500/20" :
                                        character.archetype === "Sage" ? "border-indigo-500/50 shadow-indigo-500/20" :
                                            "border-yellow-500/50 shadow-yellow-500/20"
                            )}>
                                <img
                                    src={`/assets/avatars/${character.archetype.toLowerCase()}.png`}
                                    alt={character.archetype}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            {/* Class Badge Overlay */}
                            <div className={clsx("absolute -bottom-2 -right-2 w-8 h-8 rounded-lg flex items-center justify-center border-2 border-[#020617] shadow-lg",
                                character.archetype === "Ranger" ? "bg-emerald-500 text-black" :
                                    character.archetype === "Warrior" ? "bg-red-500 text-white" :
                                        character.archetype === "Sage" ? "bg-indigo-500 text-white" :
                                            "bg-yellow-500 text-black"
                            )}>
                                <character.icon size={16} strokeWidth={2.5} />
                            </div>

                            {/* Level Badge Overlay (Top Left) */}
                            <div className="absolute -top-2 -left-2 bg-black/60 backdrop-blur border border-white/10 w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs text-white shadow-xl">
                                {character.level}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-1 flex items-center gap-1.5">
                                <character.icon size={10} className={character.color} />
                                {character.archetype} Class
                            </div>
                            <h3 className="text-2xl font-bold text-white truncate leading-none mb-2">
                                {profile.name || "Longevity Hero"}
                            </h3>

                            <div className="space-y-1.5">
                                <div className="flex justify-between text-[10px] uppercase font-bold tracking-wider">
                                    <span className="text-white/60">Level {character.level}</span>
                                    <span className="text-white/60">{Math.floor(character.progress)}%</span>
                                </div>
                                <div className="h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${character.progress}%` }}
                                        transition={{ duration: 1, ease: "easeOut" }}
                                        className={clsx("h-full rounded-full bg-gradient-to-r",
                                            character.archetype === "Ranger" ? "from-emerald-600 to-emerald-400" :
                                                character.archetype === "Warrior" ? "from-red-600 to-red-400" :
                                                    character.archetype === "Sage" ? "from-indigo-600 to-indigo-400" :
                                                        "from-yellow-600 to-yellow-400"
                                        )}
                                    />
                                </div>
                                <div className="text-[9px] text-right text-text-muted">
                                    {Math.floor(character.totalXP % 500)} / 500 XP
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Flame className="text-orange-500 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.currentStreak}</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Streak</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Star className="text-yellow-500 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.avgScore}</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Avg Score</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Activity className="text-green-500 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.totalWorkouts}</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Workouts</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Calendar className="text-blue-500 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.totalLogs}</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Days</span>
                    </div>

                    {/* New Stats */}
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Footprints className="text-cyan-400 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{(stats.totalSteps / 1000).toFixed(1)}k</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Steps</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Map className="text-emerald-400 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.totalDistance.toFixed(1)}km</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Distance</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Zap className="text-purple-500 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{(stats.totalCalories / 1000).toFixed(1)}k</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Cals</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Moon className="text-indigo-400 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.totalSleepHours}h</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Sleep</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Droplets className="text-blue-400 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{stats.totalWater}L</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Water</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 flex flex-col items-center justify-center gap-1 shadow-lg shadow-black/20">
                        <Beef className="text-red-400 w-5 h-5 mb-0.5" />
                        <span className="text-xl font-bold text-white tracking-tight">{(stats.totalProtein / 1000).toFixed(1)}kg</span>
                        <span className="text-[9px] text-text-muted uppercase tracking-wider font-semibold">Protein</span>
                    </div>
                </div>

                {/* Achievements List */}
                <div className="space-y-3">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider px-1">Badges</h3>
                    <div className="grid grid-cols-2 gap-3">
                        {achievements.map((achievement) => {
                            const isUnlocked = achievement.condition(stats);
                            return (
                                <motion.div
                                    key={achievement.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className={clsx(
                                        "relative flex flex-col items-center text-center p-4 rounded-2xl border transition-all overflow-hidden gap-2",
                                        isUnlocked
                                            ? "bg-gradient-to-br from-white/10 to-white/5 border-green-500/30 shadow-lg shadow-black/20"
                                            : "bg-white/5 border-white/5 opacity-50 grayscale"
                                    )}
                                >
                                    {isUnlocked && (
                                        <div className="absolute inset-0 bg-green-500/5 pointer-events-none" />
                                    )}

                                    <div className={clsx(
                                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0 border mb-1",
                                        isUnlocked ? "bg-black/40 border-white/10" : "bg-white/5 border-white/5"
                                    )}>
                                        <achievement.icon className={clsx("w-5 h-5", isUnlocked ? achievement.color : "text-gray-500")} />
                                    </div>

                                    <div className="flex-1 flex flex-col items-center">
                                        <h4 className={clsx("font-bold text-xs leading-tight mb-1", isUnlocked ? "text-white" : "text-gray-400")}>
                                            {achievement.title}
                                        </h4>
                                        <p className="text-[10px] text-text-muted leading-tight">
                                            {achievement.description}
                                        </p>
                                    </div>

                                    {isUnlocked && (
                                        <div className="bg-green-500/20 text-green-400 text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider border border-green-500/20 mt-1">
                                            Unlocked
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </div>
    );
}
